import { PlusIcon } from "icons/outline";
import { IconButton, cn } from "ui";

import { useRouter } from "next/router";
import PlanCard from "../../components/Subscription/PlanCard";
import EmptyPlans from "../../components/Subscription/EmptyPlans";
import NoResults from "../../components/NoResults/NoResults";
import {
  EntityCardGridSkeleton,
  SkeletonToolbar,
} from "../../components/skeletons";

import { usePaginationParams } from "../../hooks/usePaginationParams";
import { useMoneyHashQuery } from "../../hooks/useMoneyHashQuery";
import { useRestoreScrollOnFetching } from "../../hooks/useRestoreScrollOnFetching";

import Layout from "../../components/Layout";
import { Pagination } from "../../components/Pagination";
import { useGlobalSearchQuery } from "../../components/GlobalSearch";
import { axiosApiInstance } from "../api/axios";
import { ErrorPage } from "../../components/ErrorPage";
import { Filters } from "../../components/newFilters";
import { PageHeader } from "../../components/PageHeading";
import { SubscriptionNavigationTabs } from "../../components/NavigationTabs/SubscriptionNavigationTabs";

export default function PlansPage() {
  const router = useRouter();
  const [paginationParams] = usePaginationParams();
  const searchQuery = useGlobalSearchQuery();

  const {
    data: plans,
    count,
    isLoading,
    isFetching,
    error,
    isError,
    listMeta,
  } = useMoneyHashQuery({
    queryKey: ["subscriptionPlans", { ...paginationParams, ...router.query }],
    queryFn: ({ signal }) =>
      axiosApiInstance
        .get("/d/subscriptions/plans/", {
          params: {
            ...paginationParams,
            ...router.query,
          },
          signal,
        })
        .then((res) => res.data),
    keepPreviousData: true,
  });
  const scrollRef = useRestoreScrollOnFetching(isFetching);

  return (
    <Layout ref={scrollRef} metaTitle="Plans" className="flex flex-col gap-4">
      <PageHeader
        title="Manage all your plans"
        description="Create a new plans and view existing ones to make sure you are offering the best to your customers."
        actions={
          <>
            {listMeta?.add_enabled && (
              <IconButton onClick={() => router.push(`/plans/add`)}>
                <PlusIcon className="h-5 w-5" aria-hidden="true" />
              </IconButton>
            )}
          </>
        }
        withoutBorder
      />
      <SubscriptionNavigationTabs activeTab="plans" />

      {isError && <ErrorPage error={error} />}
      {!isError && (
        <>
          {!isLoading && (
            <p className="text-sm font-medium text-foundations-bold">
              {count} subscription plans
            </p>
          )}

          {(listMeta?.filter_enabled || listMeta?.search_enabled) && (
            <div>
              <Filters
                enabledFilters={listMeta?.filter_enabled}
                filters={listMeta.filter_types}
                recentStoreKey="plans"
                search={{
                  enabled: listMeta?.search_enabled,
                  placeholder: "Search Plans",
                }}
              />
            </div>
          )}

          {isLoading ? (
            <>
              <SkeletonToolbar />
              <EntityCardGridSkeleton count={+paginationParams.limit} />
            </>
          ) : (
            <div
              className={cn(
                "w-full",
                !!plans.length
                  ? "grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3 "
                  : ""
              )}
            >
              {plans.map(
                (
                  {
                    columns: {
                      amount,
                      currency,
                      id,
                      name,
                      recurrency,
                      recurrency_unit,
                    },
                  },
                  index
                ) => (
                  <PlanCard
                    key={index}
                    data={{
                      id,
                      title: name,
                      pricing: {
                        amount,
                        currency,
                        recurrency,
                        recurrency_unit,
                      },
                    }}
                    link={`/plans/${id}`}
                  />
                )
              )}

              {!plans.length && !isLoading && !searchQuery && (
                <EmptyPlans
                  onClickAdd={() => {
                    router.push(`/plans/add`);
                  }}
                />
              )}

              {!plans.length && !!searchQuery && (
                <NoResults
                  isFiltering={false}
                  addEnabled={false}
                  tableName="subscription plan"
                />
              )}
            </div>
          )}

          {count > 1 && !isLoading ? (
            <Pagination
              total={count}
              onPageChange={({ _, page, limit }) => {
                if (isLoading) return;

                router.replace({
                  query: { ...router.query, page, limit },
                });
              }}
            />
          ) : null}
        </>
      )}
    </Layout>
  );
}
PlansPage.parentPath = "/plans";
